import { useEffect, useState, useCallback } from "react";
import { api, rupee } from "@/lib/api";
import { Plus, Trash2, EyeOff, Eye, Tag } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full bg-surface border border-line rounded-sm px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-clay";

export default function AdminDashboard() {
  const [tab, setTab] = useState("categories");
  const [cats, setCats] = useState([]);
  const [occs, setOccs] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [newCat, setNewCat] = useState({ name: "", parent_id: "" });
  const [newOcc, setNewOcc] = useState("");
  const [cp, setCp] = useState({ code: "", description: "", discount_type: "percentage", value: 10, min_order: 0, max_discount: 0, start_date: "", end_date: "", usage_limit: 0, per_customer_limit: 1, scope: "all", scope_value: "", active: true });

  const load = useCallback(() => {
    api.get("/admin/categories").then(({ data }) => setCats(data));
    api.get("/admin/occasions").then(({ data }) => setOccs(data));
    api.get("/admin/coupons").then(({ data }) => setCoupons(data));
    api.get("/admin/reviews").then(({ data }) => setReviews(data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const addCat = async () => {
    if (!newCat.name.trim()) return;
    await api.post("/admin/categories", { name: newCat.name, parent_id: newCat.parent_id || null, active: true });
    toast.success("Category added"); setNewCat({ name: "", parent_id: "" }); api.get("/admin/categories").then(({ data }) => setCats(data));
  };
  const toggleCat = async (c) => { await api.put(`/admin/categories/${c.id}`, { name: c.name, parent_id: c.parent_id, active: !c.active }); load(); };
  const addOcc = async () => { if (!newOcc.trim()) return; await api.post("/admin/occasions", { name: newOcc, active: true }); toast.success("Occasion added"); setNewOcc(""); api.get("/admin/occasions").then(({ data }) => setOccs(data)); };
  const toggleOcc = async (o) => { await api.put(`/admin/occasions/${o.id}`, { name: o.name, active: !o.active }); load(); };
  const addCoupon = async () => {
    if (!cp.code.trim()) { toast.error("Code required"); return; }
    try { await api.post("/admin/coupons", { ...cp, value: Number(cp.value), min_order: Number(cp.min_order), max_discount: Number(cp.max_discount), usage_limit: Number(cp.usage_limit), per_customer_limit: Number(cp.per_customer_limit), start_date: cp.start_date || null, end_date: cp.end_date || null }); toast.success("Coupon saved"); api.get("/admin/coupons").then(({ data }) => setCoupons(data)); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delCoupon = async (id) => { await api.delete(`/admin/coupons/${id}`); setCoupons(coupons.filter((c) => c.id !== id)); };
  const hideReview = async (id) => { const { data } = await api.post(`/admin/reviews/${id}/hide`); setReviews(reviews.map((r) => r.id === id ? { ...r, hidden: data.hidden } : r)); };

  const tops = cats.filter((c) => !c.parent_id);
  const Tab = ({ v, l }) => <button onClick={() => setTab(v)} className={`px-4 py-3 text-sm border-b-2 -mb-px whitespace-nowrap ${tab === v ? "border-clay text-clay" : "border-transparent text-mute"}`} data-testid={`admin-tab-${v}`}>{l}</button>;

  return (
    <div className="pt-24 sm:pt-28 pb-24 min-h-screen">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
        <div className="overline text-clay mb-3">Admin control</div>
        <h1 className="font-serif text-4xl sm:text-6xl tracking-tighter">Platform admin</h1>
        <div className="flex gap-2 mt-8 border-b border-line overflow-x-auto">
          <Tab v="categories" l={`Categories (${cats.length})`} />
          <Tab v="occasions" l={`Occasions (${occs.length})`} />
          <Tab v="coupons" l={`Coupons (${coupons.length})`} />
          <Tab v="reviews" l={`Reviews (${reviews.length})`} />
        </div>

        {tab === "categories" && (
          <div className="mt-8 max-w-2xl">
            <div className="bg-surface border border-line rounded-sm p-5 flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[160px]"><label className="overline text-mute block mb-1.5">Name</label><input value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} className={inputCls} data-testid="admin-cat-name" /></div>
              <div className="flex-1 min-w-[160px]"><label className="overline text-mute block mb-1.5">Parent (optional)</label>
                <select value={newCat.parent_id} onChange={(e) => setNewCat({ ...newCat, parent_id: e.target.value })} className={inputCls} data-testid="admin-cat-parent">
                  <option value="">— Top level —</option>
                  {tops.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <button onClick={addCat} className="rounded-full bg-clay text-sand px-5 py-2.5 text-sm flex items-center gap-2" data-testid="admin-cat-add"><Plus className="w-4 h-4" /> Add</button>
            </div>
            <div className="mt-5 space-y-1">
              {tops.map((t) => (
                <div key={t.id}>
                  <div className="flex items-center justify-between py-2 border-b border-line">
                    <span className={`font-serif text-lg ${!t.active ? "line-through text-mute" : ""}`}>{t.name}</span>
                    <button onClick={() => toggleCat(t)} className="text-xs text-clay" data-testid={`admin-cat-toggle-${t.slug}`}>{t.active ? "Deactivate" : "Activate"}</button>
                  </div>
                  {cats.filter((c) => c.parent_id === t.id).map((c) => (
                    <div key={c.id} className="flex items-center justify-between py-1.5 pl-6 text-sm border-b border-line/60">
                      <span className={!c.active ? "line-through text-mute" : "text-ink/80"}>{c.name}</span>
                      <button onClick={() => toggleCat(c)} className="text-xs text-clay">{c.active ? "Deactivate" : "Activate"}</button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "occasions" && (
          <div className="mt-8 max-w-xl">
            <div className="bg-surface border border-line rounded-sm p-5 flex gap-3 items-end">
              <div className="flex-1"><label className="overline text-mute block mb-1.5">New occasion</label><input value={newOcc} onChange={(e) => setNewOcc(e.target.value)} className={inputCls} data-testid="admin-occ-name" /></div>
              <button onClick={addOcc} className="rounded-full bg-clay text-sand px-5 py-2.5 text-sm flex items-center gap-2" data-testid="admin-occ-add"><Plus className="w-4 h-4" /> Add</button>
            </div>
            <div className="mt-5 space-y-1">
              {occs.map((o) => (
                <div key={o.id} className="flex items-center justify-between py-2 border-b border-line">
                  <span className={!o.active ? "line-through text-mute" : ""}>{o.name}</span>
                  <button onClick={() => toggleOcc(o)} className="text-xs text-clay" data-testid={`admin-occ-toggle-${o.slug}`}>{o.active ? "Deactivate" : "Activate"}</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "coupons" && (
          <div className="mt-8">
            <div className="bg-surface border border-line rounded-sm p-5 grid sm:grid-cols-3 gap-3">
              <div><label className="overline text-mute block mb-1">Code</label><input value={cp.code} onChange={(e) => setCp({ ...cp, code: e.target.value.toUpperCase() })} className={inputCls} data-testid="admin-coupon-code" /></div>
              <div><label className="overline text-mute block mb-1">Type</label><select value={cp.discount_type} onChange={(e) => setCp({ ...cp, discount_type: e.target.value })} className={inputCls} data-testid="admin-coupon-type"><option value="percentage">Percentage</option><option value="flat">Flat ₹</option></select></div>
              <div><label className="overline text-mute block mb-1">Value</label><input type="number" value={cp.value} onChange={(e) => setCp({ ...cp, value: e.target.value })} className={inputCls} data-testid="admin-coupon-value" /></div>
              <div><label className="overline text-mute block mb-1">Min order ₹</label><input type="number" value={cp.min_order} onChange={(e) => setCp({ ...cp, min_order: e.target.value })} className={inputCls} /></div>
              <div><label className="overline text-mute block mb-1">Max discount ₹ (cap)</label><input type="number" value={cp.max_discount} onChange={(e) => setCp({ ...cp, max_discount: e.target.value })} className={inputCls} /></div>
              <div><label className="overline text-mute block mb-1">Total uses (0=∞)</label><input type="number" value={cp.usage_limit} onChange={(e) => setCp({ ...cp, usage_limit: e.target.value })} className={inputCls} /></div>
              <div><label className="overline text-mute block mb-1">Per customer</label><input type="number" value={cp.per_customer_limit} onChange={(e) => setCp({ ...cp, per_customer_limit: e.target.value })} className={inputCls} /></div>
              <div><label className="overline text-mute block mb-1">Start</label><input type="date" value={cp.start_date} onChange={(e) => setCp({ ...cp, start_date: e.target.value })} className={inputCls} /></div>
              <div><label className="overline text-mute block mb-1">Expiry</label><input type="date" value={cp.end_date} onChange={(e) => setCp({ ...cp, end_date: e.target.value })} className={inputCls} /></div>
              <div className="sm:col-span-2"><label className="overline text-mute block mb-1">Description</label><input value={cp.description} onChange={(e) => setCp({ ...cp, description: e.target.value })} className={inputCls} /></div>
              <div className="sm:col-span-3 flex justify-end"><button onClick={addCoupon} className="rounded-full bg-clay text-sand px-6 py-2.5 text-sm flex items-center gap-2" data-testid="admin-coupon-add"><Tag className="w-4 h-4" /> Create coupon</button></div>
            </div>
            <div className="mt-5 space-y-2">
              {coupons.map((c) => (
                <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 bg-surface border border-line rounded-sm px-5 py-3" data-testid={`admin-coupon-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-mono bg-clay/10 text-clay px-3 py-1 rounded-sm">{c.code}</span>
                    <span className="text-sm text-mute">{c.discount_type === "flat" ? rupee(c.value) : `${c.value}%`}{c.max_discount ? ` (cap ${rupee(c.max_discount)})` : ""} · used {c.times_used || 0}× · gave {rupee(c.total_discount_given || 0)} · rev {rupee(c.revenue_generated || 0)}</span>
                  </div>
                  <button onClick={() => delCoupon(c.id)} className="text-mute hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "reviews" && (
          <div className="mt-8 space-y-3">
            {reviews.map((r) => (
              <div key={r.id} className={`bg-surface border border-line rounded-sm px-5 py-4 ${r.hidden ? "opacity-60" : ""}`} data-testid={`admin-review-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm"><b>{r.rating}★</b> · {r.product_title} · {r.buyer_name}</div>
                  <button onClick={() => hideReview(r.id)} className="text-xs flex items-center gap-1 text-clay" data-testid={`admin-review-hide-${r.id}`}>
                    {r.hidden ? <><Eye className="w-4 h-4" /> Unhide</> : <><EyeOff className="w-4 h-4" /> Hide</>}
                  </button>
                </div>
                <p className="text-sm text-mute mt-1">{r.comment}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
